import { createContext, useContext, useMemo } from 'react'
import { UseQueryResult } from '@tanstack/react-query'
import { PageResponse } from '@core/entities/pagination'
import {
  Comment,
  CommentSecondaryReferenceObjectTypes,
} from '@core/entities/comments'
import { RequestErrorType } from '@core/utils/api-axios-instance'
import groupBy from 'lodash/groupBy'
import type { Dictionary } from 'lodash'
import {
  RequestTypes,
  useGetComments,
} from '@core/react-query/features/comments'

export type CommentsContext = {
  commentsQuery?: UseQueryResult<PageResponse<Comment>, RequestErrorType>
  commentsBySubTypeId: Dictionary<Comment[]>
  commentsPayload?: Parameters<typeof useGetComments>[0]
  createCommentCreationPayload?: (args: {
    organisationId: number
    message: string
    attachmentsKeys?: Array<string>
    projectId?: number
    subTypeIdText?: string
    subTypeId?: number
    subType?: CommentSecondaryReferenceObjectTypes
  }) => RequestTypes['createComment']
  commentsProjectId?: number
}

export type CommentsContextCreator = (params: {
  commentsPayload: Parameters<typeof useGetComments>[0]
  options?: Parameters<typeof useGetComments>[1]
  projectId: number
}) => CommentsContext

export const CommentsContext = createContext<CommentsContext>(undefined)

export const createCommentsContextValue: CommentsContextCreator = ({
  commentsPayload,
  options = {},
  projectId,
}) => {
  const { id, type, subType, subTypeId, subTypeIdText, page, size, sort } =
    commentsPayload
  const commentsQuery = useGetComments(
    { id, type, page, size, sort },
    {
      refetchOnMount: false,
      ...options,
    },
  )

  const createCommentCreationPayload = (args) => ({
    ...args,
    referenceObjectId: id,
    referenceObjectType: type,
    secondaryReferenceObjectId: args?.subTypeId || subTypeId,
    secondaryReferenceObjectIdText: args?.subTypeIdText || subTypeIdText,
    secondaryReferenceObjectType: args?.subType || subType,
  })

  const commentsBySubTypeId = useMemo(() => {
    return groupBy(
      commentsQuery.data?.content || [],
      ({ secondaryReferenceObjectId, secondaryReferenceObjectIdText }) => {
        return secondaryReferenceObjectId || secondaryReferenceObjectIdText
      },
    )
  }, [commentsQuery.data])

  return {
    commentsProjectId: projectId,
    commentsQuery,
    commentsPayload,
    createCommentCreationPayload,
    commentsBySubTypeId,
  }
}

export const useComments: () => CommentsContext = () =>
  useContext(CommentsContext) || { commentsBySubTypeId: {} }
